import { useState, useRef, useTransition, useEffect } from "react";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Send, AlertCircle, Info } from "lucide-react";
import { Input } from "../ui/input";
import { DefaultButton } from "../DefaultButton";
import { t } from "@/lib/i18n";
import { usePrivy } from "@privy-io/react-auth";
import { useStats } from "@/lib/hooks/useApi";
import { useNavigate } from "@tanstack/react-router";
import { ConvexError } from "convex/values";

export function MessageInput() {
  const { user } = usePrivy();
  const navigate = useNavigate();
  const walletAddress = user?.wallet?.address ?? "";
  const { data: stats } = useStats(walletAddress);

  const [message, setMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sendMessage = useMutation(api.messages.send);
  const updateTyping = useMutation(api.typing.updateTyping);
  const inputRef = useRef<HTMLInputElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const typingTimeoutRef = useRef<any>(null); // Using any for timeout to avoid NodeJS types issue
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const errorTimeoutRef = useRef<any>(null);
  const [isMutating, startMutating] = useTransition();

  const isUnverified = stats?.isVerified === false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    // Clear any existing error
    setError(null);

    startMutating(async () => {
      try {
        await sendMessage({ content: message.trim() });
        setMessage("");
        setIsTyping(false);

        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
      } catch (error) {
        // Handle ConvexError - show message directly
        if (error instanceof ConvexError) {
          const errorData = error.data as {
            code?: string;
            message?: string;
            reason?: string;
          };

          if (errorData?.code === "CHAT_BANNED") {
            setError(
              errorData.message ||
                errorData.reason ||
                t("chatWidget.messageInput.errors.chatBanned"),
            );
          } else {
            setError(
              errorData?.message ||
                t("chatWidget.messageInput.errors.generic"),
            );
          }

          // Auto-clear error after 5 seconds
          if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
          }
          errorTimeoutRef.current = setTimeout(() => {
            setError(null);
          }, 5000);
          return;
        }

        // Show error message
        if (error instanceof Error && error.message.includes("referral")) {
          setError(t("chatWidget.messageInput.errors.referralCodeBlocked"));
        } else {
          setError(t("chatWidget.messageInput.errors.generic"));
        }

        // Auto-clear error after 5 seconds
        if (errorTimeoutRef.current) {
          clearTimeout(errorTimeoutRef.current);
        }
        errorTimeoutRef.current = setTimeout(() => {
          setError(null);
        }, 5000);
      }
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);

    // Clear error when user starts typing
    if (error) {
      setError(null);
    }

    if (!isTyping && e.target.value.trim()) {
      setIsTyping(true);
      updateTyping({ isTyping: true }).catch(() => {});
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      updateTyping({ isTyping: false }).catch(() => {});
    }, 2000);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  // If user is unverified, show warning and CTA
  if (isUnverified) {
    return (
      <div className="space-y-2">
        {/* Verification Warning */}
        <div className="flex items-center gap-2 px-3 py-2 text-sm text-blue-50 bg-blue-950 border border-blue-800 rounded-md">
          <Info className="w-4 h-4 shrink-0" />
          <span>Verify your account to use this feature</span>
        </div>

        {/* CTA Button */}
        <DefaultButton
          onClick={() =>
            navigate({ to: "/profile", search: { socialLink: true } })
          }
          className="w-full h-10 flex items-center justify-center"
        >
          Verify Account
        </DefaultButton>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 px-3 py-2 text-sm text-red-50 bg-red-950 border border-red-800 rounded-md animate-in fade-in slide-in-from-bottom-1 duration-200">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex items-center space-x-2">
        <Input
          ref={inputRef}
          value={message}
          onChange={handleInputChange}
          placeholder={t("chatWidget.messageInput.placeholder")}
          className={`flex-1 px-4 py-2 ${error ? "border-red-500" : ""}`}
          maxLength={100}
          autoFocus={false}
          disabled={isMutating}
        />

        <DefaultButton
          type="submit"
          disabled={!message.trim() || isMutating}
          className={`p-2 transition-all duration-200 ${
            !message.trim() && "grayscale"
          }`}
        >
          <Send className="w-4 h-4" />
        </DefaultButton>
      </form>
    </div>
  );
}
